/**
 * Contract Intelligence state: reviews, their documents and gaps, reviewed
 * documents, templates and settings, from the workspace server's
 * `/api/amoreg/contract-intelligence/*` routes (`apps/server/src/amoreg/
 * contractIntelligence/http.ts`), where the tenant guard and the admin checks
 * live. Nothing here decides who may do what.
 *
 * Reads are per-organization atoms on the compliance stream
 * (`complianceQueryAtom`): the server announces every write — a gap the run
 * reported, a decision, a finding raised — so an open page refreshes as the
 * run works, without polling. A read answers `{ kind: "ready" }`, `"missing"`
 * (a 404: not this organization's, or deleted) or `"unavailable"`, never an
 * empty list for a failure: "no reviews" and "could not load" render apart.
 *
 * @module state/contractIntelligence
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { complianceQueryAtom } from "./complianceLive";

/* ------------------------------------------------------------------ */
/* Wire                                                                */
/* ------------------------------------------------------------------ */

export const CI_SEVERITIES = ["critical", "high", "medium", "low"] as const;
export type CiSeverity = (typeof CI_SEVERITIES)[number];
export const CI_REVIEW_STATUSES = ["draft", "queued", "running", "completed", "failed"] as const;
export type CiReviewStatus = (typeof CI_REVIEW_STATUSES)[number];
export const CI_BASIS_KINDS = ["policies", "regime", "template"] as const;
export type CiBasisKind = (typeof CI_BASIS_KINDS)[number];

const BasisSchema = Schema.Struct({
  kind: Schema.Literals(CI_BASIS_KINDS),
  items: Schema.Array(Schema.Struct({ id: Schema.String, name: Schema.String })),
});
export type CiBasis = typeof BasisSchema.Type;

const ReviewSchema = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  basis: BasisSchema,
  status: Schema.Literals(CI_REVIEW_STATUSES),
  attempt: Schema.Number,
  summary: Schema.NullOr(Schema.String),
  conclusion: Schema.NullOr(Schema.Literals(["no_gaps", "gaps_found"])),
  error: Schema.NullOr(Schema.String),
  progress: Schema.Struct({ read: Schema.Record(Schema.String, Schema.Number) }),
  createdByEmail: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  updatedAt: Schema.String,
  startedAt: Schema.NullOr(Schema.String),
  completedAt: Schema.NullOr(Schema.String),
});
export type CiReview = typeof ReviewSchema.Type;

const SeverityCountsSchema = Schema.Struct({
  critical: Schema.Number,
  high: Schema.Number,
  medium: Schema.Number,
  low: Schema.Number,
});

const ReviewSummarySchema = Schema.Struct({
  ...ReviewSchema.fields,
  documents: Schema.Array(
    Schema.Struct({
      id: Schema.String,
      name: Schema.String,
      counterparty: Schema.NullOr(Schema.String),
      documentType: Schema.NullOr(Schema.String),
    }),
  ),
  gaps: Schema.Struct({ open: SeverityCountsSchema, accepted: Schema.Number, raised: Schema.Number }),
});
export type CiReviewSummary = typeof ReviewSummarySchema.Type;

const DocumentSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  mediaType: Schema.String,
  byteSize: Schema.Number,
  counterparty: Schema.NullOr(Schema.String),
  documentType: Schema.NullOr(Schema.String),
  summary: Schema.NullOr(Schema.String),
  charCount: Schema.Number,
  text: Schema.String,
});
export type CiDocument = typeof DocumentSchema.Type;

const GapSchema = Schema.Struct({
  id: Schema.String,
  documentId: Schema.String,
  title: Schema.String,
  severity: Schema.Literals(CI_SEVERITIES),
  passage: Schema.String,
  startOffset: Schema.Number,
  endOffset: Schema.Number,
  rule: Schema.Struct({
    kind: Schema.Literals(CI_BASIS_KINDS),
    reference: Schema.String,
    label: Schema.String,
    quote: Schema.NullOr(Schema.String),
  }),
  explanation: Schema.String,
  recommendation: Schema.NullOr(Schema.String),
  status: Schema.Literals(["open", "accepted", "raised"]),
  decisionNote: Schema.NullOr(Schema.String),
  decidedByEmail: Schema.NullOr(Schema.String),
  decidedAt: Schema.NullOr(Schema.String),
  findingId: Schema.NullOr(Schema.String),
  findingPolicyId: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
});
export type CiGap = typeof GapSchema.Type;
export type CiGapStatus = CiGap["status"];

const ReviewDetailSchema = Schema.Struct({
  review: ReviewSchema,
  documents: Schema.Array(DocumentSchema),
  gaps: Schema.Array(GapSchema),
});
export type CiReviewDetail = typeof ReviewDetailSchema.Type;

const DocumentListingSchema = Schema.Struct({
  id: Schema.String,
  reviewId: Schema.String,
  reviewTitle: Schema.String,
  reviewStatus: Schema.Literals(CI_REVIEW_STATUSES),
  name: Schema.String,
  mediaType: Schema.String,
  byteSize: Schema.Number,
  counterparty: Schema.NullOr(Schema.String),
  documentType: Schema.NullOr(Schema.String),
  summary: Schema.NullOr(Schema.String),
  charCount: Schema.Number,
  openGaps: Schema.Number,
  highestOpenSeverity: Schema.NullOr(Schema.Literals(CI_SEVERITIES)),
  createdAt: Schema.String,
});
export type CiDocumentListing = typeof DocumentListingSchema.Type;

const TemplateItemSchema = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  requirement: Schema.String,
  severity: Schema.Literals(CI_SEVERITIES),
});
export type CiTemplateItem = typeof TemplateItemSchema.Type;

const TemplateSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  documentType: Schema.NullOr(Schema.String),
  items: Schema.Array(TemplateItemSchema),
  createdAt: Schema.String,
  updatedAt: Schema.String,
});
export type CiTemplate = typeof TemplateSchema.Type;

const SettingsSchema = Schema.Struct({
  defaultBasis: Schema.NullOr(BasisSchema),
  requireAcceptNote: Schema.Boolean,
});
export type CiSettings = typeof SettingsSchema.Type;

/* ------------------------------------------------------------------ */
/* Reads                                                               */
/* ------------------------------------------------------------------ */

export type CiRead<A> =
  | { readonly kind: "ready"; readonly value: A }
  | { readonly kind: "missing" }
  | { readonly kind: "unavailable" };

export const ciUrl = (path: string, params: Record<string, string>) =>
  resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/contract-intelligence/${path}`, params);

const TRANSIENT = new Set([502, 503, 504]);

/**
 * One read: 404 is "missing", a transient gateway answer (or a dropped
 * connection) is retried twice, anything else that is not a decodable 2xx is
 * "unavailable". A body that does not decode is answered at once, never
 * retried: the same bytes would fail the same way.
 */
export const readCi = <A>(url: string, decode: (input: unknown) => A): Effect.Effect<CiRead<A>> =>
  Effect.tryPromise({
    try: async (signal) => {
      const response = await fetch(url, { credentials: "include", signal });
      if (TRANSIENT.has(response.status)) throw new Error("transient");
      if (response.status === 404) return { kind: "missing" } as const;
      if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) {
        return { kind: "unavailable" } as const;
      }
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        return { kind: "unavailable" } as const;
      }
      try {
        return { kind: "ready", value: decode(body) } as const;
      } catch {
        return { kind: "unavailable" } as const;
      }
    },
    catch: (cause) => cause,
  }).pipe(
    Effect.retry({ times: 2 }),
    Effect.orElseSucceed((): CiRead<A> => ({ kind: "unavailable" })),
  );

const decodeReviews = Schema.decodeUnknownSync(Schema.Struct({ reviews: Schema.Array(ReviewSummarySchema) }));
const decodeDetail = Schema.decodeUnknownSync(ReviewDetailSchema);
const decodeDocuments = Schema.decodeUnknownSync(Schema.Struct({ documents: Schema.Array(DocumentListingSchema) }));
const decodeTemplates = Schema.decodeUnknownSync(Schema.Struct({ templates: Schema.Array(TemplateSchema) }));
const decodeSettings = Schema.decodeUnknownSync(Schema.Struct({ settings: SettingsSchema }));
const decodeGap = Schema.decodeUnknownSync(Schema.Struct({ gap: GapSchema }));
const decodeReview = Schema.decodeUnknownSync(Schema.Struct({ review: ReviewSchema }));
const decodeTemplate = Schema.decodeUnknownSync(Schema.Struct({ template: TemplateSchema }));

const none = <A>(): Effect.Effect<CiRead<A>> => Effect.succeed({ kind: "unavailable" });

const reviewsAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId
      ? readCi(ciUrl("reviews", { organizationId }), (json) => decodeReviews(json).reviews)
      : none<ReadonlyArray<CiReviewSummary>>(),
  ).pipe(Atom.withLabel(`web-amoreg:ci-reviews:${organizationId}`)),
);

const reviewKey = (organizationId: string, reviewId: string) => `${organizationId}\u0000${reviewId}`;

const reviewAtom = Atom.family((key: string) => {
  const [organizationId = "", reviewId = ""] = key.split("\u0000");
  return complianceQueryAtom(
    organizationId,
    organizationId && reviewId
      ? readCi(ciUrl("review", { organizationId, reviewId }), decodeDetail)
      : none<CiReviewDetail>(),
  ).pipe(Atom.withLabel(`web-amoreg:ci-review:${reviewId}`));
});

const documentsAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId
      ? readCi(ciUrl("documents", { organizationId }), (json) => decodeDocuments(json).documents)
      : none<ReadonlyArray<CiDocumentListing>>(),
  ).pipe(Atom.withLabel(`web-amoreg:ci-documents:${organizationId}`)),
);

const templatesAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId
      ? readCi(ciUrl("templates", { organizationId }), (json) => decodeTemplates(json).templates)
      : none<ReadonlyArray<CiTemplate>>(),
  ).pipe(Atom.withLabel(`web-amoreg:ci-templates:${organizationId}`)),
);

const settingsAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId
      ? readCi(ciUrl("settings", { organizationId }), (json) => decodeSettings(json).settings)
      : none<CiSettings>(),
  ).pipe(Atom.withLabel(`web-amoreg:ci-settings:${organizationId}`)),
);

export interface CiView<A> {
  /** Null until the first answer. */
  readonly read: CiRead<A> | null;
  /** A read is in flight (the first, or a refresh behind data already shown). */
  readonly isPending: boolean;
  readonly reload: () => void;
}

function useCiAtom<A>(atom: Atom.Atom<AsyncResult.AsyncResult<CiRead<A>, never>>): CiView<A> {
  const result = useAtomValue(atom);
  const value = AsyncResult.value(result);
  const reload = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  return {
    read: value._tag === "Some" ? value.value : null,
    isPending: result.waiting,
    reload,
  };
}

export const useCiReviews = (organizationId: string | null) =>
  useCiAtom(useMemo(() => reviewsAtom(organizationId ?? ""), [organizationId]));

export const useCiDocuments = (organizationId: string | null) =>
  useCiAtom(useMemo(() => documentsAtom(organizationId ?? ""), [organizationId]));

export const useCiTemplates = (organizationId: string | null) =>
  useCiAtom(useMemo(() => templatesAtom(organizationId ?? ""), [organizationId]));

export const useCiSettings = (organizationId: string | null) =>
  useCiAtom(useMemo(() => settingsAtom(organizationId ?? ""), [organizationId]));

/** How often a page holding an in-flight run re-reads it, should a stream event be missed. */
export const RUNNING_REFRESH_MS = 5_000;

/** One review; while its run is in flight it also re-reads on a slow timer as a safety net. */
export function useCiReview(organizationId: string | null, reviewId: string): CiView<CiReviewDetail> {
  const atom = useMemo(() => reviewAtom(reviewKey(organizationId ?? "", reviewId)), [organizationId, reviewId]);
  const view = useCiAtom(atom);
  const active =
    view.read?.kind === "ready" &&
    (view.read.value.review.status === "queued" || view.read.value.review.status === "running");
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => appAtomRegistry.refresh(atom), RUNNING_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [active, atom]);
  return view;
}

/** Re-read everything this organization's area shows (after a write). */
export function refreshContractIntelligence(organizationId: string, reviewId?: string): void {
  appAtomRegistry.refresh(reviewsAtom(organizationId));
  appAtomRegistry.refresh(documentsAtom(organizationId));
  if (reviewId) appAtomRegistry.refresh(reviewAtom(reviewKey(organizationId, reviewId)));
}

/* ------------------------------------------------------------------ */
/* Writes                                                              */
/* ------------------------------------------------------------------ */

export type CiWrite<A> = { readonly ok: true; readonly value: A } | { readonly ok: false; readonly message: string };

/** The server's own sentence for a refused write, or the fallback. */
export function ciErrorMessage(body: string, fallback: string): string {
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    if (typeof parsed?.message === "string" && parsed.message.trim().length > 0 && parsed.message.length < 300) {
      return parsed.message;
    }
  } catch {
    // Not JSON.
  }
  return fallback;
}

async function write<A>(
  method: "POST" | "DELETE",
  url: string,
  body: unknown,
  decode: (json: unknown) => A,
  fallback: string,
): Promise<CiWrite<A>> {
  try {
    const response = await fetch(url, {
      method,
      credentials: "include",
      headers: { "content-type": "application/json" },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const text = await response.text();
    if (!response.ok) return { ok: false, message: ciErrorMessage(text, fallback) };
    return { ok: true, value: decode(JSON.parse(text)) };
  } catch {
    return { ok: false, message: fallback };
  }
}

export interface CiDocumentUpload {
  readonly name: string;
  readonly mediaType: string;
  readonly byteSize: number;
  readonly sha256: string;
  readonly counterparty: string | null;
  readonly text: string;
}

export const createCiReview = (
  organizationId: string,
  input: {
    readonly title: string | null;
    readonly basis: CiBasis;
    readonly documents: ReadonlyArray<CiDocumentUpload>;
    readonly run: boolean;
  },
) =>
  write(
    "POST",
    ciUrl("reviews", { organizationId }),
    input,
    (json) => decodeReview(json).review,
    "The review could not be created. Try again.",
  ).then((outcome) => {
    if (outcome.ok) refreshContractIntelligence(organizationId);
    return outcome;
  });

export const runCiReview = (organizationId: string, reviewId: string) =>
  write(
    "POST",
    ciUrl("review/run", { organizationId, reviewId }),
    undefined,
    (json) => decodeReview(json).review,
    "The review could not start. Try again.",
  ).then((outcome) => {
    refreshContractIntelligence(organizationId, reviewId);
    return outcome;
  });

export const deleteCiReview = (organizationId: string, reviewId: string) =>
  write("DELETE", ciUrl("review", { organizationId, reviewId }), undefined, () => true, "The review could not be deleted.").then(
    (outcome) => {
      if (outcome.ok) refreshContractIntelligence(organizationId);
      return outcome;
    },
  );

export const decideCiGap = (
  organizationId: string,
  reviewId: string,
  gapId: string,
  action: "accept" | "reopen",
  note: string | null,
) =>
  write(
    "POST",
    ciUrl("gap", { organizationId, gapId }),
    { action, ...(note ? { note } : {}) },
    (json) => decodeGap(json).gap,
    action === "accept" ? "The gap could not be accepted." : "The gap could not be reopened.",
  ).then((outcome) => {
    refreshContractIntelligence(organizationId, reviewId);
    return outcome;
  });

export const raiseCiGap = (organizationId: string, reviewId: string, gapId: string, policyId: string) =>
  write(
    "POST",
    ciUrl("gap/raise", { organizationId, gapId }),
    { policyId },
    (json) => ({ gap: decodeGap(json).gap, findingId: String((json as { findingId?: unknown }).findingId ?? "") }),
    "The finding could not be raised. Try again.",
  ).then((outcome) => {
    refreshContractIntelligence(organizationId, reviewId);
    return outcome;
  });

export const saveCiTemplate = (
  organizationId: string,
  templateId: string | null,
  input: {
    readonly name: string;
    readonly description: string | null;
    readonly documentType: string | null;
    readonly items: ReadonlyArray<Omit<CiTemplateItem, "id"> & { readonly id?: string }>;
  },
) =>
  write(
    "POST",
    ciUrl("templates", templateId ? { organizationId, templateId } : { organizationId }),
    input,
    (json) => decodeTemplate(json).template,
    "The template could not be saved.",
  ).then((outcome) => {
    appAtomRegistry.refresh(templatesAtom(organizationId));
    return outcome;
  });

export const archiveCiTemplate = (organizationId: string, templateId: string) =>
  write("DELETE", ciUrl("templates", { organizationId, templateId }), undefined, () => true, "The template could not be removed.").then(
    (outcome) => {
      appAtomRegistry.refresh(templatesAtom(organizationId));
      return outcome;
    },
  );

export const saveCiSettings = (organizationId: string, settings: CiSettings) =>
  write(
    "POST",
    ciUrl("settings", { organizationId }),
    settings,
    (json) => decodeSettings(json).settings,
    "The settings could not be saved.",
  ).then((outcome) => {
    appAtomRegistry.refresh(settingsAtom(organizationId));
    return outcome;
  });
